import React, { useState, useEffect } from 'react';
import { numaraListesiAyristir, basilamazNedeni, kovaAdi, secimSinifi, SECILI_ISARET } from './yardimcilar';
import { logoSvgOlustur } from './CiktiPaneli';
import { SB_TASARIMLAR, SB_TURLER, SB_TEST_TAVAN, SB_TEST_SAYISI, SB_KUNYE } from './sabitler';
import KazanimSecici from './KazanimSecici';
import { DERSLER, kazanimSozleri } from '../../lib/dersler';

// ============================================================
// SORU BANKASI paneli (2026-10-11, kullanıcı kararları): tasarım panelde serbest seçilir, testler
// numara listesiyle elle kurulur (her test: ad + soru numaraları). Sorular POST /api/sorular/batch
// ile gelir; basılamayan soru nedeniyle listelenir (numara listesiyle aynı kural, basilamazNedeni),
// bağlam çiftinin eksik kardeşi tek tıkla eklenir. Tür etiketi sunucuda otomatik; burada elle
// değiştirilebilir. Sunucu: /basim/soru-bankasi (services/soruBankasi.js → texlive-api HTML motoru).
// ============================================================

export const yeniTest = () => ({ ad: '', metin: '', sorular: [], tur: {}, bulunamayan: [], gecersiz: [] });

// Testin basılabilirlik özeti (saf; testte sınanır).
export function sbTestOzet(test, yayineviId = null) {
  const sorunlar = [];
  (test.gecersiz || []).forEach((t) => sorunlar.push({ no: t, neden: 'geçersiz numara' }));
  (test.bulunamayan || []).forEach((n) => sorunlar.push({ no: n, neden: 'bulunamadı' }));
  (test.sorular || []).forEach((s) => {
    const n = basilamazNedeni(s, yayineviId);
    if (n) sorunlar.push({ no: Number(s.public_no), neden: n });
  });
  const grup = (s) => (s.kaynak_detay && s.kaynak_detay.baglam_grubu) || null;
  const gruplar = new Set((test.sorular || []).map(grup).filter(Boolean));
  if (gruplar.size > 1) sorunlar.push({ no: '—', neden: `bir testte en çok bir bağlam çifti olabilir (${gruplar.size} grup)` });
  const eksikKardes = (test.sorular || [])
    .filter((s) => Array.isArray(s.grup_eksik_uyeler) && s.grup_eksik_uyeler.length > 0)
    .map((s) => ({ no: Number(s.public_no), kardesler: s.grup_eksik_uyeler.map((k) => Number(k.public_no)) }));
  if (eksikKardes.length) eksikKardes.forEach((e) => sorunlar.push({ no: e.no, neden: `bağlam kardeşi eksik (#${e.kardesler.join(', #')})` }));
  return { sorunlar, eksikKardes, adet: (test.sorular || []).length, baglamVar: gruplar.size > 0 };
}

// Basım kilidi (Basim.js basimKilidi): null = basılabilir.
export function sbKilidi(sb, yayineviId = null) {
  const dolu = (sb.testler || []).filter((t) => (t.sorular || []).length > 0);
  if (dolu.length === 0) return 'test eklenmedi';
  if ((sb.testler || []).some((t) => String(t.metin || '').trim() && t.metin !== t.getirilen)) return 'numaraları getirilmemiş test var (Getir)';
  const sorunlu = (sb.testler || []).findIndex((t) => sbTestOzet(t, yayineviId).sorunlar.length > 0);
  if (sorunlu >= 0) return `Test ${sorunlu + 1}: basılamayan soru var`;
  if (dolu.some((t) => t.sorular.length > SB_TEST_TAVAN)) return `test başına en çok ${SB_TEST_TAVAN} soru`;
  const hepsi = dolu.flatMap((t) => t.sorular.map((s) => s.id));
  if (new Set(hepsi).size !== hepsi.length) return 'aynı soru birden fazla testte';
  return null;
}

// Seçilen soruları testlere böl (saf; testte sınanır): zorluk kovasına göre sıralanır (kararlı), sonra
// testlere sırayla dağıtılır → her test kolaydan zora, testlerin boyu ve zorluğu dengeli.
export function sbTestlereBol(sorular, testBasi) {
  if (!sorular.length) return [];
  const t = Math.ceil(sorular.length / Math.max(1, Math.min(SB_TEST_TAVAN, testBasi)));
  const sira = { kolay: 0, orta: 1, zor: 2 };
  const k = (s) => sira[kovaAdi(s.zorluk_puan)] ?? 1;
  const testler = Array.from({ length: t }, () => []);
  [...sorular].sort((a, b) => k(a) - k(b)).forEach((s, i) => testler[i % t].push(s));
  return testler;
}

// GEÇMİŞTEN YENİDEN BASIM (2026-10-20, saf; testte sınanır): soru bankası kaydının test bölümü varsa aynen;
// yoksa (deneme kaydı) numaralar sırayla bölünür — 40'a kadar tek test, üstünde test başına 20 (en çok 60 test).
export function gecmisSbTestleri(kayit) {
  if (Array.isArray(kayit.sb_testler) && kayit.sb_testler.length > 0) {
    return kayit.sb_testler.map((t) => ({ ad: t.ad || '', nolar: t.nolar || [], tur: t.tur || {} }));
  }
  const nolar = kayit.public_no_listesi || [];
  const boy = nolar.length <= SB_TEST_TAVAN ? nolar.length : Math.max(20, Math.ceil(nolar.length / SB_TEST_SAYISI));
  const testler = [];
  for (let i = 0; i < nolar.length; i += boy) testler.push({ ad: '', nolar: nolar.slice(i, i + boy), tur: {} });
  return testler;
}

// OTOMATİK DOLDURMA (2026-10-13, kullanıcı isteği "müfredat + sınıf + ders + öğrenme çıktısı + soru sayısı
// seçerek soru bankası"): sorular dengeli seçim ucuyla seçilir (POST /basim/dengeli-secim — ÖÇ'lere eşit,
// zorluk oranlı, basılabilir havuz), test başına adede bölünüp mevcut dolu testlerin ARDINA eklenir
// (zaten testteki numaralar dışlanır → 9.1, sonra 9.2 ayrı bloklar). Sınıf: Kitap satırındaki seçim.
function OtoDoldur({ apiUrl, sb, setSb, yayineviId }) {
  const [mufredat, setMufredat] = useState('Maarif_2024');
  const [ders, setDers] = useState('tyt_matematik');
  const [kazanim, setKazanim] = useState(null);   // { kod, ad } | null
  const [adet, setAdet] = useState(100);
  const [testBasi, setTestBasi] = useState(20);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [sonuc, setSonuc] = useState(null);       // { mesaj, uyarilar } | { hata }
  const soz = kazanimSozleri(mufredat);
  const istek = (yol, govde) => {
    const token = localStorage.getItem('yks_token');
    return fetch(`${apiUrl}${yol}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(govde),
    }).then((r) => r.json().catch(() => ({})).then((j) => ({ ok: r.ok, j })));
  };

  const doldur = async () => {
    const eski = sb.testler.filter((t) => t.sorular.length > 0 || String(t.metin || '').trim());
    setYukleniyor(true); setSonuc(null);
    try {
      const govde = {
        sinav: ders.startsWith('ayt_') ? 'AYT' : 'TYT',
        dersler: [{ ders, adet: Number(adet), ...(kazanim ? { kazanimlar: [kazanim.kod] } : {}) }],
        mufredat, cift: 0,
        haric: eski.flatMap((t) => t.sorular.map((x) => Number(x.public_no))).slice(0, 1000),
      };
      if ([9, 10, 11, 12].includes(Number(sb.sinif))) govde.sinif = Number(sb.sinif);
      if (yayineviId != null) govde.yayineviId = yayineviId;
      const d = await istek('/api/boru-hatti/basim/dengeli-secim', govde);
      if (!d.ok || !d.j.basarili) throw new Error(d.j.hata || 'seçim yapılamadı');
      const nolar = (d.j.dersler || []).flatMap((x) => x.sabitNolar || []);
      if (!nolar.length) throw new Error(`Uygun soru yok (${(d.j.uyarilar || []).join(' · ') || 'havuz boş'})`);
      const b = await istek('/api/sorular/batch', { public_nolar: nolar });
      if (!Array.isArray(b.j.sorular)) throw new Error(b.j.hata || 'sorular alınamadı');
      const byNo = new Map(b.j.sorular.map((s) => [Number(s.public_no), s]));
      const parcalar = sbTestlereBol(nolar.filter((n) => byNo.has(n)).map((n) => byNo.get(n)), Number(testBasi));
      if (eski.length + parcalar.length > SB_TEST_SAYISI) throw new Error(`Kitap başına en çok ${SB_TEST_SAYISI} test (test başına soruyu artırın)`);
      const ad = kazanim ? kazanim.ad : '';
      const yeni = parcalar.map((sorular, i) => {
        const metin = sorular.map((s) => s.public_no).join(', ');
        return { ...yeniTest(), ad: ad && parcalar.length > 1 ? `${ad} ${i + 1}` : ad, metin, getirilen: metin, sorular };
      });
      const dersAd = ((DERSLER.find((x) => x.value === ders) || {}).label || '').replace(/^(TYT|AYT)\s+/, '');
      setSb({ ...sb, ...(eski.length ? {} : { ders: dersAd || sb.ders }), testler: [...eski, ...yeni] });
      setSonuc({ mesaj: `${nolar.length} soru → ${yeni.length} test eklendi`, uyarilar: d.j.uyarilar || [] });
    } catch (e) {
      setSonuc({ hata: e.message });
    }
    setYukleniyor(false);
  };

  const secim = 'px-2 py-1.5 rounded-lg border border-gray-300 bg-white text-xs';
  const gecerli = Number(adet) >= 1 && Number(adet) <= 250 && Number(testBasi) >= 5 && Number(testBasi) <= SB_TEST_TAVAN;
  return (
    <div data-sb-oto className="rounded-lg border border-violet-200 bg-violet-50/40 px-3 py-2 space-y-1.5">
      <div className="flex flex-wrap items-end gap-2">
        <span className="text-[10px] font-bold uppercase text-gray-500 self-center">Otomatik doldur</span>
        <select data-sb-oto-mufredat value={mufredat} onChange={(e) => { setMufredat(e.target.value); setKazanim(null); }} aria-label="Müfredat" className={secim}>
          <option value="Maarif_2024">Maarif</option>
          <option value="MEB_2018">MEB 2018</option>
        </select>
        <select data-sb-oto-ders value={ders} onChange={(e) => { setDers(e.target.value); setKazanim(null); }} aria-label="Ders" className={secim}>
          {DERSLER.filter((x) => x.value).map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
        </select>
        <div className="min-w-[220px]">
          <KazanimSecici key={`${ders}|${mufredat}`} apiUrl={apiUrl} ders={ders} mufredat={mufredat}
            value={kazanim ? kazanim.kod : ''} onChange={(k) => { if (!k) setKazanim(null); }}
            onSecilenBilgi={(x) => setKazanim(x)} />
        </div>
        <label className="text-[11px] text-gray-600">Soru
          <input data-sb-oto-adet type="number" min={1} max={250} value={adet} onChange={(e) => setAdet(e.target.value)}
            className="ml-1 w-16 px-1.5 py-1 rounded border border-gray-300 text-xs" />
        </label>
        <label className="text-[11px] text-gray-600">Test başına
          <input data-sb-oto-test-basi type="number" min={5} max={SB_TEST_TAVAN} value={testBasi} onChange={(e) => setTestBasi(e.target.value)}
            className="ml-1 w-14 px-1.5 py-1 rounded border border-gray-300 text-xs" />
        </label>
        <button type="button" data-sb-oto-doldur onClick={doldur} disabled={yukleniyor || !gecerli}
          className="text-xs px-3 py-1.5 rounded-lg bg-violet-600 text-white font-semibold hover:bg-violet-700 disabled:opacity-40">
          {yukleniyor ? '⏳ Seçiliyor…' : 'Testleri doldur'}
        </button>
      </div>
      <div className="text-[10px] text-gray-500">
        {kazanim ? `${kazanim.kod} ${kazanim.ad}` : `Tüm ${soz.adlar}`}
        {Number(sb.sinif) ? ` · ${sb.sinif}. sınıf` : ' · sınıf seçilmedi (Kitap satırından seçin)'}
        {` · ${soz.adlar} eşit dağılır, zorluk %30/%40/%30, yalnız basılabilir sorular; dolu testlerin ardına eklenir`}
      </div>
      {sonuc && sonuc.hata && <div data-sb-oto-hata className="text-[11px] text-red-700 font-semibold">{sonuc.hata}</div>}
      {sonuc && !sonuc.hata && (
        <div data-sb-oto-sonuc className="text-[11px] text-gray-700">
          ✓ {sonuc.mesaj}
          {sonuc.uyarilar.map((u) => <div key={u} className="text-amber-800">⚠ {u}</div>)}
        </div>
      )}
    </div>
  );
}

function TestKart({ apiUrl, yayineviId, test, index, toplam, onDegis, onSil, onTasi }) {
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState('');
  const getir = async (metin = test.metin) => {
    const { nolar, gecersiz } = numaraListesiAyristir(metin, SB_TEST_TAVAN + 1);
    setHata('');
    if (nolar.length === 0) { onDegis({ ...test, metin, getirilen: metin, sorular: [], bulunamayan: [], gecersiz }); return; }
    if (nolar.length > SB_TEST_TAVAN) { setHata(`Test başına en çok ${SB_TEST_TAVAN} soru`); return; }
    setYukleniyor(true);
    try {
      const token = localStorage.getItem('yks_token');
      const r = await fetch(`${apiUrl}/api/sorular/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ public_nolar: nolar }),
      });
      const d = await r.json();
      if (!Array.isArray(d.sorular)) throw new Error(d.hata || `HTTP ${r.status}`);
      const byNo = new Map(d.sorular.map((s) => [Number(s.public_no), s]));
      onDegis({
        ...test, metin, getirilen: metin,
        sorular: nolar.filter((n) => byNo.has(n)).map((n) => byNo.get(n)),
        bulunamayan: nolar.filter((n) => !byNo.has(n)),
        gecersiz,
      });
    } catch (e) {
      setHata(`Sorular alınamadı: ${e.message}`);
    }
    setYukleniyor(false);
  };
  // Taslaktan açılan test: numaralar saklı, soru gövdeleri değil → bir kez yeniden getirilir.
  useEffect(() => {
    if (String(test.metin || '').trim() && test.metin !== test.getirilen) getir();
  }, []);
  const ozet = sbTestOzet(test, yayineviId);
  const kardesleriEkle = () => {
    const ek = ozet.eksikKardes.flatMap((e) => e.kardesler).filter((n) => !test.sorular.some((s) => Number(s.public_no) === n));
    getir(`${test.metin.trim()}, ${[...new Set(ek)].join(', ')}`);
  };
  const turYaz = (pn, v) => {
    const tur = { ...test.tur };
    if (v) tur[pn] = v; else delete tur[pn];
    onDegis({ ...test, tur });
  };
  return (
    <div className="rounded-lg border border-gray-200 p-2.5" data-sb-test={index + 1}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-bold text-white bg-teal-700 rounded px-1.5 py-0.5">TEST {index + 1}</span>
        <input type="text" value={test.ad} maxLength={80} onChange={(e) => onDegis({ ...test, ad: e.target.value })}
          placeholder={`Test adı (boşsa “Test ${index + 1}”) — ör. Sayılar`} aria-label={`Test ${index + 1} adı`}
          className="flex-1 min-w-[180px] px-2 py-1 rounded border border-gray-300 text-xs" />
        <button type="button" disabled={index === 0} onClick={() => onTasi(-1)} title="Yukarı" className="px-1.5 text-xs disabled:opacity-30">↑</button>
        <button type="button" disabled={index === toplam - 1} onClick={() => onTasi(1)} title="Aşağı" className="px-1.5 text-xs disabled:opacity-30">↓</button>
        <button type="button" disabled={toplam === 1} onClick={onSil} title="Testi sil" className="px-1.5 text-xs text-red-600 disabled:opacity-30">✕</button>
      </div>
      <div className="mt-1.5 flex items-start gap-2">
        <textarea data-sb-numaralar value={test.metin} rows={2} onChange={(e) => onDegis({ ...test, metin: e.target.value })}
          placeholder="Soru numaraları: 2538, 2715, 39640 … ya da 40100-40110 (kolaydan zora sırayla)"
          aria-label={`Test ${index + 1} soru numaraları`}
          className="flex-1 px-2 py-1 rounded border border-gray-300 text-xs font-mono" />
        <button type="button" data-sb-getir onClick={() => getir()} disabled={yukleniyor || !test.metin.trim()}
          className="px-2.5 py-1.5 rounded bg-indigo-600 text-white text-xs font-semibold disabled:opacity-40">
          {yukleniyor ? '…' : 'Getir'}
        </button>
      </div>
      {hata && <div className="mt-1 text-[11px] text-red-700 font-semibold">{hata}</div>}
      {ozet.sorunlar.length > 0 && (
        <div data-sb-sorun className="mt-1.5 rounded border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-800">
          Basılamaz: {ozet.sorunlar.map((s) => `#${s.no} ${s.neden}`).join(' · ')}
          {ozet.eksikKardes.length > 0 && (
            <button type="button" data-sb-kardes onClick={kardesleriEkle} className="ml-2 underline font-semibold">kardeşleri ekle</button>
          )}
        </div>
      )}
      {test.sorular.length > 0 && (
        <table className="mt-1.5 w-full text-[11px]">
          <thead><tr className="text-gray-500 text-left"><th>#</th><th>No</th><th>Öğrenme çıktısı</th><th>Zorluk</th><th>Cevap</th><th>Tür</th></tr></thead>
          <tbody>
            {test.sorular.map((s, i) => (
              <tr key={s.id} className="border-t border-gray-100">
                <td>{i + 1}</td>
                <td className="font-mono">#{s.public_no}</td>
                <td className="font-mono">{s.kazanim_kodu || '—'}</td>
                <td>{kovaAdi(s.zorluk_puan) || '—'}{s.zorluk_puan ? ` (${s.zorluk_puan})` : ''}</td>
                <td>{s.dogru_cevap}</td>
                <td>
                  <select value={test.tur[s.public_no] || ''} onChange={(e) => turYaz(s.public_no, e.target.value)}
                    aria-label={`#${s.public_no} türü`} className="text-[11px] border border-gray-200 rounded">
                    <option value="">otomatik</option>
                    {SB_TURLER.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {ozet.baglamVar && <div className="mt-1 text-[10px] text-gray-500">Bağlam çifti testin sonunda ortak kutuyla basılır.</div>}
    </div>
  );
}

export default function SoruBankasiPaneli({ apiUrl, sb, setSb, yayineviId = null }) {
  const [logoDurum, setLogoDurum] = useState('');
  const alan = (k) => (e) => setSb({ ...sb, [k]: e.target.value });
  const testDegis = (i, t) => setSb({ ...sb, testler: sb.testler.map((x, j) => (j === i ? t : x)) });
  const testTasi = (i, yon) => {
    const y = [...sb.testler];
    [y[i], y[i + yon]] = [y[i + yon], y[i]];
    setSb({ ...sb, testler: y });
  };
  const logoYukle = async (e) => {
    const dosya = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!dosya) return;
    try {
      setSb({ ...sb, logoSvg: atob(await logoSvgOlustur(dosya)) });
      setLogoDurum('');
    } catch (err) {
      setLogoDurum(err.message);
    }
  };
  const toplam = sb.testler.reduce((a, t) => a + t.sorular.length, 0);
  return (
    <div className="mt-2 space-y-3" data-soru-bankasi>
      <div>
        <div className="text-[10px] font-bold uppercase text-gray-500 mb-1">Tasarım</div>
        <div className="flex flex-wrap gap-2">
          {SB_TASARIMLAR.map((t) => (
            <button key={t.id} type="button" data-sb-tasarim={t.id} aria-pressed={sb.tasarim === t.id}
              onClick={() => setSb({ ...sb, tasarim: t.id })} title={t.aciklama}
              className={`${secimSinifi(sb.tasarim === t.id)} inline-flex items-center gap-1.5`}>
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: t.renk }} />
              {sb.tasarim === t.id ? SECILI_ISARET : ''}{t.ad}
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-[10px] font-bold uppercase text-gray-500">Kitap</span>
        <input type="text" value={sb.ders} onChange={alan('ders')} maxLength={40} placeholder="Ders (boşsa sorulardan)" aria-label="Ders adı"
          className="px-2 py-1.5 rounded-lg border border-gray-300 w-36" />
        <select value={String(sb.sinif)} onChange={alan('sinif')} aria-label="Sınıf" className="px-2 py-1.5 rounded-lg border border-gray-300 bg-white">
          <option value="">Sınıf yok</option>
          {[9, 10, 11, 12].map((n) => <option key={n} value={String(n)}>{n}. sınıf</option>)}
        </select>
        <input type="text" data-sb-baslik value={sb.baslik} onChange={alan('baslik')} maxLength={80} placeholder="Kitap adı (ör. Soru Bankası)"
          aria-label="Kitap adı" className="px-2 py-1.5 rounded-lg border border-gray-300 w-52" />
        <input type="text" value={sb.altBaslik} onChange={alan('altBaslik')} maxLength={120} placeholder="Kapak alt satırı (isteğe bağlı)"
          aria-label="Kapak alt satırı" className="px-2 py-1.5 rounded-lg border border-gray-300 w-60" />
        <input type="text" value={sb.yil} onChange={alan('yil')} maxLength={20} placeholder="Yıl" aria-label="Yıl"
          className="px-2 py-1.5 rounded-lg border border-gray-300 w-24" />
        <label className="px-2 py-1.5 rounded border border-blue-300 bg-blue-50 text-blue-800 font-semibold hover:bg-blue-100 cursor-pointer"
          title="Kapaktaki yayınevi logosu alanı: PNG, JPG ya da SVG (bu basıma özel; taslakta saklanır)">
          <input data-sb-logo type="file" accept="image/png,image/jpeg,image/svg+xml" className="sr-only" onChange={logoYukle} />
          {sb.logoSvg ? '✓ Logo · değiştir' : '🖼 Logo'}
        </label>
        {sb.logoSvg && <button type="button" onClick={() => setSb({ ...sb, logoSvg: null })} className="text-[11px] text-gray-500 underline">logoyu kaldır</button>}
        {logoDurum && <span className="text-[11px] text-red-700 font-semibold">{logoDurum}</span>}
      </div>
      <details className="text-xs" data-sb-kunye-kutu>
        <summary className="cursor-pointer text-[10px] font-bold uppercase text-gray-500">
          Künye {SB_KUNYE.every(([a]) => String((sb.kunye || {})[a] || '').trim()) ? '✓' : "(eksik — baskı PDF'i üretilmez)"}
        </summary>
        <div className="mt-1 flex flex-wrap gap-2">
          {SB_KUNYE.map(([a, et]) => (
            <input key={a} type="text" data-sb-kunye={a} value={(sb.kunye || {})[a] || ''} maxLength={200} placeholder={et} aria-label={et}
              onChange={(e) => setSb({ ...sb, kunye: { ...(sb.kunye || {}), [a]: e.target.value } })}
              className="px-2 py-1.5 rounded-lg border border-gray-300 w-56" />
          ))}
        </div>
      </details>
      <OtoDoldur apiUrl={apiUrl} sb={sb} setSb={setSb} yayineviId={yayineviId} />
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase text-gray-500">Testler</span>
          <span className="text-[11px] text-gray-600">{sb.testler.length} test · {toplam} soru</span>
          <button type="button" data-sb-test-ekle disabled={sb.testler.length >= SB_TEST_SAYISI}
            onClick={() => setSb({ ...sb, testler: [...sb.testler, yeniTest()] })}
            className="ml-auto px-2 py-1 rounded bg-white border border-gray-300 text-xs font-semibold hover:bg-gray-50 disabled:opacity-40">+ Test ekle</button>
        </div>
        {sb.testler.map((t, i) => (
          <TestKart key={i} apiUrl={apiUrl} yayineviId={yayineviId} test={t} index={i} toplam={sb.testler.length}
            onDegis={(x) => testDegis(i, x)} onTasi={(yon) => testTasi(i, yon)}
            onSil={() => setSb({ ...sb, testler: sb.testler.filter((_, j) => j !== i) })} />
        ))}
      </div>
    </div>
  );
}
